import { useState, useEffect, useCallback, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import api from '../api';
import { useLab } from '../i18n/LabContext';
import { useLang } from '../i18n/LanguageContext';

const THEMES_BASE = (() => {
  const base = (process.env.REACT_APP_API_URL || 'http://localhost:3000/api');
  return base.replace(/\/api(\/.*)?$/, '');
})() + '/themes';

const THEMES = [
  { key: 'hud',     name: 'HUD Dark',       descKey: 'themeHudDesc',     file: null,          tags: ['Default', 'Dark'],           color: '#27E0FF', free: true  },
  { key: 'design1', name: 'SENET Style',     descKey: 'themeDesign1Desc', file: 'design1.html', tags: ['Dark', 'Modern'],           color: '#4F8CFF', free: false },
  { key: 'design2', name: 'ggLeap Style',    descKey: 'themeDesign2Desc', file: 'design2.html', tags: ['Dark', 'Esports'],          color: '#7C3AED', free: false },
  { key: 'design3', name: 'PS5 Dock',        descKey: 'themeDesign3Desc', file: 'design3.html', tags: ['Console', 'Sleek'],         color: '#0070D1', free: false },
  { key: 'design4', name: 'Sidebar',         descKey: 'themeDesign4Desc', file: 'design4.html', tags: ['Sidebar', 'Compact'],       color: '#10B981', free: false },
  { key: 'design5', name: 'Immersiv',        descKey: 'themeDesign5Desc', file: 'design5.html', tags: ['Fullscreen', 'Cinematic'],  color: '#F59E0B', free: false },
  { key: 'design6', name: 'Turnir Esports',  descKey: 'themeDesign6Desc', file: 'design6.html', tags: ['Tournament', 'Esports'],    color: '#EF4444', free: false },
  { key: 'design7', name: 'Compact List',    descKey: 'themeDesign7Desc', file: 'design7.html', tags: ['Compact', 'List'],          color: '#6366F1', free: false },
  { key: 'design8', name: 'Hub Markaz',      descKey: 'themeDesign8Desc', file: 'design8.html', tags: ['Hub', 'Grid'],              color: '#EC4899', free: false },
  { key: 'design9', name: 'Reklama',         descKey: 'themeDesign9Desc', file: 'design9.html', tags: ['Ads', 'Banner'],            color: '#F97316', free: false },
  { key: 'design10', name: 'Minimal',        descKey: 'themeDesign10Desc',file: 'design10.html',tags: ['Minimal', 'Clean'],         color: '#84CC16', free: false },
];

function fmt(num, lang) {
  const loc = lang === 'ru' ? 'ru-RU' : 'uz-UZ';
  return Number(num).toLocaleString(loc) + " so'm";
}

// ── Buy Modal ────────────────────────────────────────────────────────────────
const PROVIDERS = [
  { id: 'click', label: 'Click',  color: '#0078FF', logo: '🔵' },
  { id: 'payme', label: 'Payme',  color: '#00C26C', logo: '🟢' },
];

const POLL_TIMEOUT_MS = 10 * 60 * 1000; // 10 daqiqa

function BuyModal({ theme, price, onClose, onSuccess }) {
  const { lang, t } = useLang();
  const [step, setStep]         = useState('choose');
  const [provider, setProvider] = useState('click');
  const [busy, setBusy]         = useState(false);
  const [payUrl, setPayUrl]     = useState(null);
  const [, setTxId]         = useState(null);
  const [devMode, setDevMode]   = useState(false);
  const [contact, setContact]   = useState(null);
  const [error, setError]       = useState(null);
  const [paid, setPaid]         = useState(false);
  const [expired, setExpired]   = useState(false);
  const pollRef    = useRef(null);
  const timeoutRef = useRef(null);

  const stopPoll = () => {
    if (pollRef.current)    { clearInterval(pollRef.current);   pollRef.current = null; }
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
  };
  useEffect(() => () => stopPoll(), []);

  useEffect(() => {
    const h = (e) => {
      if (e.key !== 'Escape') return;
      if (step === 'qr' && !paid) { setStep('choose'); stopPoll(); setExpired(false); }
      else if (step !== 'qr') onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose, step, paid]);

  const startPoll = (id) => {
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.post('/owner/themes/buy/check', { tx_id: id });
        if (r.data.paid) { stopPoll(); setPaid(true); setTimeout(() => onSuccess(theme.key), 1200); }
      } catch (e) { console.warn('poll:', e?.message); }
    }, 2500);
    // 10 daqiqadan keyin to'xtat
    timeoutRef.current = setTimeout(() => {
      stopPoll();
      setExpired(true);
    }, POLL_TIMEOUT_MS);
  };

  const handlePay = async () => {
    setBusy(true); setError(null);
    try {
      const r = await api.post('/owner/themes/buy', { theme_key: theme.key, provider });
      if (r.data.already_purchased) { onSuccess(theme.key); return; }
      if (r.data.granted)           { onSuccess(theme.key); return; }
      if (r.data.payment_url) {
        setPayUrl(r.data.payment_url);
        setTxId(r.data.tx_id);
        setDevMode(!!r.data.dev_mode);
        setExpired(false);
        setStep('qr');
        startPoll(r.data.tx_id);
      }
    } catch (e) {
      const d = e.response?.data;
      if (d?.code === 'NO_CREDENTIALS') { setContact(d.contact); setStep('contact'); }
      else { setError(d?.error || "Xatolik yuz berdi"); }
    } finally { setBusy(false); }
  };

  const goBack = () => { stopPoll(); setStep('choose'); setPayUrl(null); setTxId(null); setExpired(false); };

  const prov = PROVIDERS.find(p => p.id === provider);

  return (
    <div
      onClick={() => { if (step !== 'qr') onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9100,
        background: 'rgba(0,0,0,0.85)', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(8px)',
      }}
    >
      <div onClick={e => e.stopPropagation()} style={{
        width: step === 'qr' ? 380 : 420,
        background: '#0f0f18',
        borderRadius: 20, overflow: 'hidden',
        border: `1.5px solid ${theme.color}50`,
        boxShadow: `0 0 80px ${theme.color}15, 0 32px 80px rgba(0,0,0,0.8)`,
        transition: 'width 0.3s',
      }}>

        {/* Top color bar */}
        <div style={{ height: 4, background: `linear-gradient(90deg, ${theme.color}, ${prov?.color || theme.color})` }} />

        <div style={{ padding: '24px 28px 28px' }}>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: `${theme.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, border: `1.5px solid ${theme.color}40`, flexShrink: 0 }}>🎨</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 800 }}>{theme.name}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 1 }}>{t('launcherLicense')}</div>
            </div>
            {step !== 'qr' && (
              <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 7, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            )}
          </div>

          {/* ── STEP: choose ── */}
          {step === 'choose' && (
            <>
              {/* Price badge */}
              <div style={{ background: `${theme.color}10`, border: `1px solid ${theme.color}25`, borderRadius: 12, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>{t('launcherPaySum')}</span>
                <span style={{ fontSize: 22, fontWeight: 900, color: theme.color }}>{fmt(price)}</span>
              </div>

              {/* Provider tabs */}
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>{t('launcherPayMethod')}</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                {PROVIDERS.map(p => (
                  <button key={p.id} onClick={() => setProvider(p.id)} style={{
                    flex: 1, padding: '12px 8px', borderRadius: 12, cursor: 'pointer',
                    fontWeight: 700, fontSize: 13, transition: 'all 0.15s',
                    background: provider === p.id ? `${p.color}22` : 'rgba(255,255,255,0.03)',
                    border: `2px solid ${provider === p.id ? p.color : 'rgba(255,255,255,0.08)'}`,
                    color: provider === p.id ? p.color : 'rgba(255,255,255,0.4)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                  }}>
                    <span style={{ fontSize: 22 }}>{p.logo}</span>
                    <span>{p.label}</span>
                  </button>
                ))}
              </div>

              {error && (
                <div style={{ fontSize: 12, color: '#F87171', background: 'rgba(248,113,113,0.08)', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>{error}</div>
              )}

              <button onClick={handlePay} disabled={busy} style={{
                width: '100%', padding: '13px', borderRadius: 12, cursor: 'pointer',
                background: busy ? 'rgba(255,255,255,0.08)' : `linear-gradient(135deg, ${prov?.color}, ${prov?.color}bb)`,
                border: 'none', color: busy ? 'rgba(255,255,255,0.4)' : '#fff',
                fontSize: 14, fontWeight: 800, letterSpacing: 0.3,
                boxShadow: busy ? 'none' : `0 0 24px ${prov?.color}40`,
                transition: 'all 0.2s',
              }}>
                {busy ? t('launcherPreparing') : `${prov?.logo} ${prov?.label} ${t('launcherPayVia')}`}
              </button>
            </>
          )}

          {/* ── STEP: qr ── */}
          {step === 'qr' && (
            <div style={{ textAlign: 'center' }}>
              {paid ? (
                /* To'lov muvaffaqiyatli */
                <div style={{ padding: '20px 0' }}>
                  <div style={{ fontSize: 56, marginBottom: 12 }}>✅</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#34D399', marginBottom: 6 }}>{t('launcherPayOk')}</div>
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>{theme.name} {t('launcherActivatingMsg')}</div>
                </div>
              ) : (
                <>
                  {/* QR instruction */}
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 16 }}>
                    {prov?.logo} <b style={{ color: prov?.color }}>{prov?.label}</b> {t('launcherScanQr')}
                  </div>

                  {/* QR code */}
                  <div style={{
                    display: 'inline-block', padding: 14, borderRadius: 16,
                    background: '#fff',
                    boxShadow: `0 0 40px ${prov?.color}40`,
                    marginBottom: 16,
                  }}>
                    <QRCodeSVG
                      value={payUrl || 'https://cybernet.uz'}
                      size={200}
                      bgColor="#ffffff"
                      fgColor="#000000"
                      level="M"
                      includeMargin={false}
                    />
                  </div>

                  {/* Amount badge */}
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 10, background: `${prov?.color}15`, border: `1px solid ${prov?.color}30`, marginBottom: 16 }}>
                    <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>{t('launcherAmountLabel')}</span>
                    <span style={{ fontSize: 16, fontWeight: 900, color: prov?.color }}>{fmt(price, lang)}</span>
                  </div>

                  {/* Dev mode badge */}
                  {devMode && (
                    <div style={{ fontSize: 11, color: '#F59E0B', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, padding: '6px 12px', marginBottom: 12 }}>
                      🧪 Test rejim — to'lov ~3 soniyada avtomatik tasdiqlandi
                    </div>
                  )}

                  {/* Status */}
                  {expired ? (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 12, color: '#F87171', background: 'rgba(248,113,113,0.08)', borderRadius: 8, padding: '10px 14px', marginBottom: 8 }}>
                        {t('launcherQrExpired')}
                      </div>
                      <button onClick={handlePay} disabled={busy} style={{
                        width: '100%', padding: '10px', borderRadius: 10, cursor: 'pointer',
                        background: `${prov?.color}22`, border: `1px solid ${prov?.color}50`,
                        color: prov?.color, fontWeight: 700, fontSize: 13,
                      }}>{t('launcherNewQr')}</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 16 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#34D399', display: 'inline-block', animation: 'qrpulse 1.4s ease-in-out infinite' }} />
                      <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{t('launcherChecking')}</span>
                    </div>
                  )}

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <a href={payUrl} target="_blank" rel="noopener noreferrer" style={{
                      flex: 1, padding: '9px', borderRadius: 10, textDecoration: 'none',
                      background: `${prov?.color}15`, border: `1px solid ${prov?.color}30`,
                      color: prov?.color, fontSize: 12, fontWeight: 700, textAlign: 'center',
                    }}>{t('launcherOpenBrowser')}</a>
                    <button onClick={goBack} style={{
                      flex: 1, padding: '9px', borderRadius: 10, cursor: 'pointer',
                      background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
                      color: 'rgba(255,255,255,0.4)', fontSize: 12, fontWeight: 600,
                    }}>{t('launcherGoBack')}</button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── STEP: contact ── */}
          {step === 'contact' && (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>📞</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>
                {t('launcherNoConfig')}<br />{t('launcherNoConfigContact')}
              </div>
              <div style={{ fontSize: 22, fontWeight: 900, color: theme.color, letterSpacing: 1, marginBottom: 20 }}>{contact}</div>
              <button onClick={onClose} style={{ padding: '10px 28px', borderRadius: 10, background: `${theme.color}20`, border: `1px solid ${theme.color}50`, color: theme.color, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>{t('launcherCloseBtn')}</button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes qrpulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.75); }
        }
      `}</style>
    </div>
  );
}

// ── Preview Modal ────────────────────────────────────────────────────────────
function PreviewModal({ theme, allThemes, onClose, onSelect, currentKey, purchased, onBuy, t }) {
  const idx = allThemes.findIndex(x => x.key === theme.key);
  const hasPrev = idx > 0;
  const hasNext = idx < allThemes.length - 1;
  const goPrev = useCallback(() => { if (hasPrev) onSelect(allThemes[idx - 1]); }, [hasPrev, idx, allThemes, onSelect]);
  const goNext = useCallback(() => { if (hasNext) onSelect(allThemes[idx + 1]); }, [hasNext, idx, allThemes, onSelect]);

  useEffect(() => {
    const h = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose, goPrev, goNext]);

  const isActive = currentKey === theme.key;
  const isPurchased = theme.free || purchased.includes(theme.key);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 9000,
      background: 'rgba(0,0,0,0.92)', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(6px)',
    }}>
      {/* Header */}
      <div onClick={e => e.stopPropagation()} style={{
        width: '92vw', maxWidth: 1280,
        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0 12px',
      }}>
        <button onClick={goPrev} disabled={!hasPrev} style={{
          width: 36, height: 36, borderRadius: 8, flexShrink: 0,
          background: hasPrev ? 'rgba(255,255,255,0.1)' : 'transparent',
          border: '1px solid rgba(255,255,255,0.12)',
          color: hasPrev ? '#fff' : 'rgba(255,255,255,0.2)',
          cursor: hasPrev ? 'pointer' : 'default', fontSize: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>‹</button>

        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: theme.color, boxShadow: `0 0 8px ${theme.color}` }} />
            <span style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>{theme.name}</span>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{idx + 1} / {allThemes.length}</span>
            {isActive && (
              <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 5, background: 'rgba(0,212,255,0.15)', color: '#27E0FF', border: '1px solid rgba(0,212,255,0.3)', fontWeight: 700 }}>{t('launcherActiveBadge')}</span>
            )}
            {!isPurchased && (
              <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 5, background: 'rgba(248,113,113,0.15)', color: '#F87171', border: '1px solid rgba(248,113,113,0.3)', fontWeight: 700 }}>{t('launcherNotPurchased')}</span>
            )}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>
            {t('launcherNavHint')}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {!isPurchased && (
            <button onClick={() => { onClose(); onBuy(theme); }} style={{
              padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
              background: `${theme.color}22`, border: `1px solid ${theme.color}60`,
              color: theme.color, fontSize: 13, fontWeight: 700,
            }}>{t('launcherBuyBtn')}</button>
          )}
          <button onClick={onClose} style={{
            padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
            background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
            color: '#fff', fontSize: 13, fontWeight: 600,
          }}>{t('launcherCloseBtnLabel')}</button>
        </div>

        <button onClick={goNext} disabled={!hasNext} style={{
          width: 36, height: 36, borderRadius: 8, flexShrink: 0,
          background: hasNext ? 'rgba(255,255,255,0.1)' : 'transparent',
          border: '1px solid rgba(255,255,255,0.12)',
          color: hasNext ? '#fff' : 'rgba(255,255,255,0.2)',
          cursor: hasNext ? 'pointer' : 'default', fontSize: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>›</button>
      </div>

      {/* iframe */}
      <div onClick={e => e.stopPropagation()} style={{
        width: '92vw', maxWidth: 1280, height: '80vh',
        background: '#000', borderRadius: 12, overflow: 'hidden',
        border: `2px solid ${isActive ? '#27E0FF' : 'rgba(255,255,255,0.1)'}`,
        boxShadow: isActive ? '0 0 30px rgba(39,224,255,0.2)' : '0 0 60px rgba(0,0,0,0.8)',
        position: 'relative',
      }}>
        {theme.file ? (
          <iframe key={theme.key} src={`${THEMES_BASE}/${theme.file}`} title={theme.name}
            style={{ width: '100%', height: '100%', border: 'none' }}
            sandbox="allow-scripts allow-same-origin" />
        ) : (
          <HudPreview />
        )}
        {!isPurchased && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'rgba(0,0,0,0.55)', display: 'flex',
            flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16,
          }}>
            <div style={{ fontSize: 48 }}>🔒</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>{t('launcherNotPurchasedMsg')}</div>
            <button onClick={() => { onClose(); onBuy(theme); }} style={{
              padding: '12px 32px', borderRadius: 12, cursor: 'pointer',
              background: `linear-gradient(135deg, ${theme.color}, ${theme.color}aa)`,
              border: 'none', color: '#000', fontSize: 15, fontWeight: 800,
            }}>{t('launcherBuyBtn')}</button>
          </div>
        )}
      </div>

      {/* Thumbnail strip */}
      <div onClick={e => e.stopPropagation()} style={{
        display: 'flex', gap: 6, marginTop: 12,
        overflowX: 'auto', maxWidth: '92vw', padding: '4px 0',
      }}>
        {allThemes.map((th) => {
          const isPurch = th.free || purchased.includes(th.key);
          return (
            <button key={th.key} onClick={() => onSelect(th)} style={{
              width: 72, height: 44, borderRadius: 6, overflow: 'hidden',
              border: `2px solid ${th.key === theme.key ? th.color : 'rgba(255,255,255,0.1)'}`,
              background: '#000', cursor: 'pointer', flexShrink: 0, position: 'relative',
              opacity: th.key === theme.key ? 1 : 0.6, transition: 'all 0.15s',
            }}>
              {th.file ? (
                <iframe src={`${THEMES_BASE}/${th.file}`} title={th.name}
                  style={{ width: '400%', height: '400%', transform: 'scale(0.25)', transformOrigin: '0 0', border: 'none', pointerEvents: 'none' }}
                  sandbox="allow-scripts allow-same-origin" />
              ) : (
                <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg,#070709,#0b0b0f)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🎮</div>
              )}
              {!isPurch && (
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>🔒</div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── HUD placeholder ──────────────────────────────────────────────────────────
function HudPreview() {
  return (
    <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg,#070709 0%,#0b0b0f 50%,#070709 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
      <div style={{ fontSize: 64 }}>🎮</div>
      <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 28, fontWeight: 900, background: 'linear-gradient(135deg,#27E0FF,#9B5CFF)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>HUD Dark</div>
      <div style={{ fontSize: 14, color: '#8A8AA2', textAlign: 'center', maxWidth: 400 }}>Asosiy launcher dizayni. Barcha kerakli funksiyalar bilan jihozlangan.</div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function LauncherThemes() {
  const { labId, currentLab, refreshLabs } = useLab();
  const { t, lang }                         = useLang();

  const [previewTheme, setPreviewTheme]     = useState(null);
  const [buyTheme, setBuyTheme]             = useState(null);
  const [saving, setSaving]                 = useState(false);
  const [successKey, setSuccessKey]         = useState(null);
  const [purchased, setPurchased]           = useState([]);
  const [themePrice, setThemePrice]         = useState(99000);
  const [loadingPurchases, setLoadingPurch] = useState(true);
  const [purchaseError, setPurchaseError]   = useState(false);

  const current = currentLab?.launcher_theme || 'hud';

  useEffect(() => {
    setLoadingPurch(true);
    api.get('/owner/themes/purchases')
      .then(r => {
        setPurchased(r.data.purchased || []);
        if (r.data.price) setThemePrice(r.data.price);
        setPurchaseError(false);
      })
      .catch(() => setPurchaseError(true))
      .finally(() => setLoadingPurch(false));
  }, []);

  const isPurchased = (key) => {
    const th = THEMES.find(th2 => th2.key === key);
    return th?.free || purchased.includes(key);
  };

  const openBuy = (theme) => {
    if (!labId) { alert(t('launcherNoBranchAlert')); return; }
    setBuyTheme(theme);
  };

  const pickTheme = async (theme) => {
    if (!labId) { alert(t('launcherNoBranchAlert')); return; }
    if (!isPurchased(theme.key)) { openBuy(theme); return; }
    setSaving(true);
    try {
      await api.patch(`/owner/labs/${labId}/settings`, { launcher_theme: theme.key });
      await refreshLabs();
      setSuccessKey(theme.key);
      setTimeout(() => setSuccessKey(null), 3000);
    } catch (e) {
      alert(e.response?.data?.error || t('error'));
    } finally { setSaving(false); }
  };

  const handlePurchaseSuccess = async (themeKey) => {
    setBuyTheme(null);
    setPurchased(prev => prev.includes(themeKey) ? prev : [...prev, themeKey]);
    setSuccessKey(themeKey);
    setTimeout(() => setSuccessKey(null), 3000);
    if (labId) {
      try {
        await api.patch(`/owner/labs/${labId}/settings`, { launcher_theme: themeKey });
        await refreshLabs();
      } catch (e) {
        alert(e.response?.data?.error || t('error'));
      }
    }
  };

  const previewableThemes = THEMES;

  return (
    <div style={{ paddingBottom: 40 }}>

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, letterSpacing: -0.5 }}>
          🎨 {t('launcherTitle') || 'Launcher Dizaynlari'}
        </h1>
        <div style={{ color: 'var(--text2)', fontSize: 13, marginTop: 6, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span>{t('launcherBranchLabel')} <b style={{ color: 'var(--cyan)' }}>{currentLab?.name || '—'}</b></span>
          <span style={{ color: 'var(--border)' }}>·</span>
          <span>{t('launcherActiveLabel')} <b style={{ color: 'var(--green)' }}>{THEMES.find(th => th.key === current)?.name || current}</b></span>
          <span style={{ color: 'var(--border)' }}>·</span>
          {loadingPurchases ? (
            <span style={{ color: 'var(--text3)', fontStyle: 'italic' }}>{t('launcherLoadingPurch')}</span>
          ) : purchaseError ? (
            <span style={{ color: '#F87171', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => { setLoadingPurch(true); api.get('/owner/themes/purchases').then(r => { setPurchased(r.data.purchased || []); setPurchaseError(false); }).catch(() => setPurchaseError(true)).finally(() => setLoadingPurch(false)); }}>
              {t('launcherLoadError')}
            </span>
          ) : (
            <span style={{ color: 'var(--text3)' }}>{t('launcherPurchasedCount')} <b style={{ color: 'var(--cyan)' }}>{purchased.length} {t('launcherPurchasedUnit')}</b></span>
          )}
        </div>
      </div>

      {/* Faol tema spotlight */}
      {(() => {
        const active = THEMES.find(th => th.key === current);
        if (!active) return null;
        return (
          <div style={{
            background: 'var(--card2)', border: `1.5px solid ${active.color}`,
            borderRadius: 14, padding: '16px 20px', marginBottom: 28,
            display: 'flex', alignItems: 'center', gap: 16,
            boxShadow: `0 0 24px ${active.color}20`,
          }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: `${active.color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, border: `1.5px solid ${active.color}60`, flexShrink: 0 }}>🎮</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700 }}>{active.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>{t(active.descKey) || active.name}</div>
            </div>
            <div style={{ padding: '5px 14px', borderRadius: 8, background: `${active.color}18`, color: active.color, border: `1px solid ${active.color}40`, fontSize: 11, fontWeight: 800, letterSpacing: 1 }}>{t('launcherActiveBadge')}</div>
            {active.file && (
              <button onClick={() => setPreviewTheme(active)} style={{ padding: '8px 16px', borderRadius: 8, cursor: 'pointer', background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border)', color: 'var(--text)', fontSize: 13 }}>
                {t('launcherPreviewBtn')}
              </button>
            )}
          </div>
        );
      })()}

      {/* Tema grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
        {THEMES.map(th => {
          const isActive  = current === th.key;
          const isSuccess = successKey === th.key;
          const owned     = isPurchased(th.key);

          return (
            <div key={th.key} style={{
              background: 'var(--card2)',
              border: `1.5px solid ${isActive ? th.color : isSuccess ? 'var(--green)' : 'rgba(255,255,255,0.07)'}`,
              borderRadius: 14, overflow: 'hidden', position: 'relative',
              transition: 'border-color 0.2s, box-shadow 0.2s',
              boxShadow: isActive ? `0 0 20px ${th.color}25` : 'none',
            }}>
              {/* Badge */}
              {isActive && (
                <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 2, background: th.color, color: '#000', fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 5 }}>{t('launcherActiveBadge')}</div>
              )}
              {isSuccess && !isActive && (
                <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 2, background: 'var(--green)', color: '#000', fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 5 }}>{t('launcherSelectedBadge')}</div>
              )}
              {!owned && !loadingPurchases && (
                <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 2, background: 'rgba(0,0,0,0.7)', color: '#F87171', fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 5, border: '1px solid rgba(248,113,113,0.3)' }}>{t('launcherBuyBadge')}</div>
              )}

              {/* Thumbnail */}
              <div
                style={{ height: 170, background: '#000', overflow: 'hidden', position: 'relative', cursor: 'pointer' }}
                onClick={() => setPreviewTheme(th)}
              >
                {th.file ? (
                  <>
                    <iframe src={`${THEMES_BASE}/${th.file}`} title={th.name}
                      loading="lazy"
                      style={{ width: '400%', height: '400%', transform: 'scale(0.25)', transformOrigin: '0 0', border: 'none', pointerEvents: 'none' }}
                      sandbox="allow-scripts allow-same-origin" />
                    <div className="thumb-hover" style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}>
                      <span className="thumb-label" style={{ padding: '8px 18px', borderRadius: 8, background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: 13, fontWeight: 700, opacity: 0, border: '1px solid rgba(255,255,255,0.2)', transition: 'opacity 0.2s' }}>
                        {t('launcherPreviewBtn')}
                      </span>
                    </div>
                  </>
                ) : (
                  <HudPreview />
                )}
                {!owned && th.file && (
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }} />
                )}
                {/* Color bar */}
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${th.color}, transparent)` }} />
              </div>

              {/* Info */}
              <div style={{ padding: '14px 16px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: th.color, boxShadow: `0 0 6px ${th.color}`, flexShrink: 0 }} />
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{th.name}</div>
                  {th.free && (
                    <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: 'rgba(52,211,153,0.15)', color: '#34D399', border: '1px solid rgba(52,211,153,0.3)', fontWeight: 800 }}>{t('launcherFree')}</span>
                  )}
                </div>

                <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 10, minHeight: 30, lineHeight: 1.5 }}>
                  {t(th.descKey) || th.name}
                </div>

                {/* Tags */}
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 12 }}>
                  {th.tags.map(tag => (
                    <span key={tag} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: `${th.color}15`, color: th.color, border: `1px solid ${th.color}30`, fontWeight: 600 }}>{tag}</span>
                  ))}
                </div>

                {/* Buttons */}
                <div style={{ display: 'flex', gap: 6 }}>
                  {th.file && (
                    <button
                      onClick={() => setPreviewTheme(th)}
                      style={{ flex: 1, padding: '9px', borderRadius: 8, cursor: 'pointer', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border)', color: 'var(--text2)', fontSize: 12, fontWeight: 600, transition: 'all 0.15s' }}
                      onMouseOver={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'var(--text)'; }}
                      onMouseOut={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = 'var(--text2)'; }}
                    >{t('launcherPreviewBtn')}</button>
                  )}

                  {owned ? (
                    <button
                      onClick={() => pickTheme(th)}
                      disabled={isActive || saving}
                      style={{
                        flex: th.file ? 1.5 : 2, padding: '9px 14px', borderRadius: 8,
                        fontSize: 12, fontWeight: 700,
                        cursor: isActive ? 'default' : 'pointer',
                        background: isActive ? `${th.color}18` : `linear-gradient(135deg, ${th.color}, ${th.color}aa)`,
                        border: isActive ? `1px solid ${th.color}40` : 'none',
                        color: isActive ? th.color : '#000',
                        opacity: saving && !isActive ? 0.6 : 1,
                      }}
                    >{isActive ? t('launcherSelectedBtn') : saving ? '...' : t('launcherSelectBtn')}</button>
                  ) : (
                    <button
                      onClick={() => openBuy(th)}
                      disabled={loadingPurchases}
                      style={{
                        flex: th.file ? 1.5 : 2, padding: '9px 14px', borderRadius: 8,
                        fontSize: 12, fontWeight: 800,
                        cursor: loadingPurchases ? 'default' : 'pointer',
                        background: loadingPurchases ? 'rgba(255,255,255,0.05)' : `linear-gradient(135deg, ${th.color}33, ${th.color}22)`,
                        border: `1.5px solid ${loadingPurchases ? 'rgba(255,255,255,0.1)' : th.color + '60'}`,
                        color: loadingPurchases ? 'rgba(255,255,255,0.2)' : th.color,
                        transition: 'all 0.15s',
                      }}
                      onMouseOver={e => { if (!loadingPurchases) e.currentTarget.style.background = `linear-gradient(135deg, ${th.color}55, ${th.color}44)`; }}
                      onMouseOut={e => { if (!loadingPurchases) e.currentTarget.style.background = `linear-gradient(135deg, ${th.color}33, ${th.color}22)`; }}
                    >{loadingPurchases ? '...' : `💳 ${fmt(themePrice, lang)}`}</button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Preview Modal */}
      {previewTheme && (
        <PreviewModal
          theme={previewTheme}
          allThemes={previewableThemes}
          onClose={() => setPreviewTheme(null)}
          onSelect={setPreviewTheme}
          currentKey={current}
          purchased={purchased}
          onBuy={(th) => { setPreviewTheme(null); setBuyTheme(th); }}
          t={t}
        />
      )}

      {/* Buy Modal */}
      {buyTheme && (
        <BuyModal
          theme={buyTheme}
          price={themePrice}
          onClose={() => setBuyTheme(null)}
          onSuccess={handlePurchaseSuccess}
        />
      )}

      <style>{`
        .thumb-hover:hover { background: rgba(0,0,0,0.45) !important; }
        .thumb-hover:hover .thumb-label { opacity: 1 !important; }
      `}</style>
    </div>
  );
}
